import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';

import { ApiError } from '@/api/ApiError';
import { environment } from '@/config/environment';
import { ManagerSessionProvider } from '@/features/auth/components/ManagerSessionProvider';
import { useManagerContext } from '@/features/auth/hooks/useManagerContext';

import type { PropsWithChildren } from 'react';

export function SessionBoundary({ children }: PropsWithChildren) {
  const contextQuery = useManagerContext();

  if (contextQuery.isPending) {
    return <SessionLoading />;
  }

  if (contextQuery.isError) {
    const status = contextQuery.error instanceof ApiError ? contextQuery.error.status : undefined;
    if (status === 401) {
      return <SignInRequired />;
    }
    if (status === 403) {
      return <AccessDenied />;
    }
    return <SessionError retry={() => void contextQuery.refetch()} />;
  }

  return <ManagerSessionProvider context={contextQuery.data}>{children}</ManagerSessionProvider>;
}

function SessionLoading() {
  return (
    <FullPagePanel>
      <CircularProgress aria-label="Loading manager session" size={32} />
      <Typography variant="h2">Loading PoolAppy</Typography>
      <Typography color="text.secondary">Confirming your secure manager session…</Typography>
    </FullPagePanel>
  );
}

function SignInRequired() {
  const signInUrl = new URL(environment.loginUrl);
  signInUrl.searchParams.set('return_url', window.location.origin);

  return (
    <FullPagePanel>
      <LockOutlinedIcon color="primary" sx={{ fontSize: 40 }} />
      <Typography variant="h1">Sign in required</Typography>
      <Typography color="text.secondary" textAlign="center">
        Sign in with your PoolAppy manager account to continue.
      </Typography>
      <Button variant="contained" href={signInUrl.toString()}>
        Go to secure sign in
      </Button>
    </FullPagePanel>
  );
}

function AccessDenied() {
  return (
    <FullPagePanel>
      <ErrorOutlineIcon color="warning" sx={{ fontSize: 40 }} />
      <Typography variant="h1">Manager access required</Typography>
      <Typography color="text.secondary" textAlign="center">
        Your account does not have permission to use the administration application.
      </Typography>
    </FullPagePanel>
  );
}

function SessionError({ retry }: { retry: () => void }) {
  return (
    <FullPagePanel>
      <Alert severity="error" sx={{ width: '100%' }}>
        PoolAppy could not verify your session. Check the server connection and try again.
      </Alert>
      <Button variant="outlined" onClick={retry}>
        Try again
      </Button>
    </FullPagePanel>
  );
}

function FullPagePanel({ children }: PropsWithChildren) {
  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'grid',
        placeItems: 'center',
        p: 2,
      }}
    >
      <Paper variant="outlined" sx={{ width: '100%', maxWidth: 480, p: 4 }}>
        <Stack spacing={2.5} alignItems="center">
          {children}
        </Stack>
      </Paper>
    </Box>
  );
}
